/**
 * "The Pending changes queue just changed" — one browser event, announced by
 * the API client itself after every write that changes the queue (stage,
 * stage a suggestion, discard, apply, requeue), whoever called it: the row
 * menu, an inline cell, a suggestion chip, the Pending tray, the Suggestions
 * page.
 *
 * Why an event and not a cache invalidation: `invalidatePrefix` marks queries
 * stale WITHOUT refetching them (lib/query/invalidate.ts — the hidden
 * keep-alive panes must not refetch their 50k-row reads for nobody), so a
 * small read that must follow the queue — the Advertising hub's campaign
 * workspace (its "Waiting in Pending changes" line and the modifier it shows)
 * and its "ClickPay suggests" chips (a staged suggestion is settled) — stayed
 * stale until the view remounted. Those readers subscribe here
 * (`usePendingEditsChanged`) and refetch themselves.
 */
import { useEffect } from 'react'

export const PENDING_EDITS_CHANGED_EVENT = 'clickpay:pending-edits-changed'

/** Tell every listener the queue changed. A no-op outside a browser. */
export function announcePendingEditsChanged(): void {
  const w = (globalThis as { window?: Window }).window
  if (!w || typeof w.dispatchEvent !== 'function') return
  w.dispatchEvent(new Event(PENDING_EDITS_CHANGED_EVENT))
}

/** `p`, announcing the change once it succeeds (a failed write changed nothing). */
export function afterPendingWrite<T>(p: Promise<T>): Promise<T> {
  return p.then(r => {
    announcePendingEditsChanged()
    return r
  })
}

/** Run `onChange` after every change to the queue. Pass a stable callback. */
export function usePendingEditsChanged(onChange: () => void): void {
  useEffect(() => {
    const w = (globalThis as { window?: Window }).window
    if (!w || typeof w.addEventListener !== 'function') return
    w.addEventListener(PENDING_EDITS_CHANGED_EVENT, onChange)
    return () => w.removeEventListener(PENDING_EDITS_CHANGED_EVENT, onChange)
  }, [onChange])
}
